// composables/configuration.js
/**
 * Composable for managing the application configuration
 */

import { isObject, snakeToCamel } from '@/modules/utils';
import { ref, onMounted, reactive } from 'vue';
import { mergeWith } from 'lodash-es'
const basePath = import.meta.env.BASE_URL || '/';

const mainVarsToLoad = {
    app_name: 'shacl-vue',
    page_title: 'shacl-vue',
    shapes_url: '',
    data_url: '',
    class_url: '',
    show_shapes_wo_id: true,
    show_classes: [],
    show_classes_with_prefix: [],
    hide_classes: [],
    hide_classes_with_prefix: [],
    no_edit_classes: [],
    allow_edit_instances: [],
    allow_copy_record_urls: true,
    editor_selection: {},
    filter_records_by: [
        "skos:prefLabel",
        "shaclvue:displayLabel",
        "dlthings:pid"
    ],
    component_config: {
        W3CISO8601YearEditor: {
            yearStart: 1925,
            yearEnd: 2077
        },
        W3CISO8601YearMonthEditor: {
            yearStart: 1925,
            yearEnd: 2077
        },
        W3CISO8601DateTimeEditor: {
            yearStart: 1925,
            yearEnd: 2077
        },
        InstancesSelectEditor: {
            fetchingsRecordsText: "Fetching records...",
        },
        PropertyShapeEditor: {
            recordNumberStepSize: 5,
        },
        NodeShapeViewer: {
            recordNumberStepSize: 5,
            textTruncateWidth: "85%",
        },
        URIEditor: {
            default: "curie",
        },
    },
    content: {},
    display_name_autogenerate: {},
    display_name_autogenerate_placeholder: {
        default: "[?]"
    },
    id_iri: "",
    id_autogenerate: {},
    id_autogenerate_override: false,
    prefixes: {},
    class_icons: {},
    documentation_url: 'https://psychoinformatics-de.github.io/shacl-vue/docs/',
    source_code_url: 'https://github.com/psychoinformatics-de/shacl-vue',
    app_theme: {
        link_color: '#41b883',
        hover_color: '#1565C0',
        active_color: '#D32F2F',
        panel_color: '#41b883',
        settings_color: '#1565C0',
        visited_color: '#41b882',
        logo: 'shacl_vue_logo.svg',
    },
    front_page_content: "",
    id_resolves_externally: [],
    use_token: false,
    token_info: '',
    token_info_url: '',
    use_service: false,
    service_base_url: [],
    service_constrained_search: {
        min_characters: 4,
        typing_debounce: 800,
    },
    class_name_display: 'name',
    footer_links: [],
    gitannex_p2phttp_config: {},
};

function mergeCustomizer(objValue, srcValue) {
    if (!Array.isArray(objValue) || !Array.isArray(srcValue)) {
        return undefined // let mergeWith handle it without a customizer
    }
    // Both are arrays, now inspect contents
    const objHasObjects = objValue.some((el) => {
        return isObject(el)
    })
    const srcHasObjects = srcValue.some((el) => {
        return isObject(el)
    })
    // If arrays contain objects, override completely
    if (objHasObjects || srcHasObjects) {
        return srcValue
    }
    // Else, return merged array of primitives (strings, numbers, etc)
    return [...new Set([...objValue, ...srcValue])]
}

export function useConfig(url) {
    const defaultURL = `${basePath}config.json`;
    var configURL;
    if (url) {
        if (url.indexOf('http')) {
            configURL = url;
        } else {
            configURL = `${basePath}${url}`;
        }
    } else {
        configURL = defaultURL;
    }
    const config = ref(null);
    const configFetched = ref(false);
    const configError = ref(false);
    const configVarsMain = reactive({});
    for (const [key, value] of Object.entries(mainVarsToLoad)) {
        configVarsMain[snakeToCamel(key)] = value;
    }

    onMounted(async () => {
        try {
            const response = await fetch(configURL, { cache: 'no-cache' });
            if (!response.ok) {
                configError.value = true;
                throw new Error(
                    `Error fetching config file: ${response.statusText}`
                );
            }
            let mainConfig = await response.json();
            let externalConfig = {};
            if (mainConfig.external_config_url) {
                let externalConfigLoaded = await loadContent(mainConfig.external_config_url, 'json')
                if (externalConfigLoaded !== null) {
                    externalConfig = externalConfigLoaded;
                }
            }
            config.value = mergeWith(structuredClone(externalConfig), mainConfig, mergeCustomizer)
            configFetched.value = true;
        } catch (error) {
            console.error('Fetch error:', error);
            configError.value = true;
            throw error;
        }
    });

    async function loadContent(url, format = 'json') {
        if (!url) {
            return null
        }
        try {
            const response = await fetch(url, { cache: 'no-cache' });
            if (!response.ok) {
                console.error(`Error fetching content: ${response.statusText}`)
                return null
            }
            if (format == 'text') {
                return await response.text();
            } else {
                return await response.json();
            }
        } catch (error) {
            console.error('Error fetching content:', error);
            return null
        }
    }

    function loadConfigVars() {
        // only supports one level of recursion into objects
        for (const [key, val] of Object.entries(mainVarsToLoad)) {
            if (config.value.hasOwnProperty(key)) {
                if (isObject(config.value[key])) {
                    configVarsMain[snakeToCamel(key)] = val;
                    for (const [k, v] of Object.entries(config.value[key])) {
                        configVarsMain[snakeToCamel(key)][k] = v
                    }
                } else {
                    configVarsMain[snakeToCamel(key)] = config.value[key];
                }
            }
        }
    }

    async function loadAllContent() {
        if (configVarsMain.content && Object.keys(configVarsMain.content).length > 0) {
            for (const src of Object.keys(configVarsMain.content)) {
                configVarsMain.content[src].value = await loadContent(configVarsMain.content[src].ur, 'text')
            }
        }
    }

    return {
        config,
        configFetched,
        configError,
        configVarsMain,
        loadConfigVars,
        loadAllContent,
    };
}
